import { useEffect, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { motion, type Variants } from 'motion/react'
import clsx from 'clsx'
import { store } from '@/src/stores/store'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { GetGameDocument } from '@/src/lib/graphql/generated/graphql'
import Logo from '../ui/Logo'
import TextHeadline from '../ui/texts/TextHeadline'
import PreloaderTippCarousel from '../ui/PreloaderTippCarousel'
import { containerVariants, tabButtonVariants, tabContainerVariants } from '@/src/lib/motionVariants'
import { useSlideInWooshSound } from '@/src/game/audio/uiSounds'
import TabControls from '../ui/controls/TabControls'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconSettings from '../ui/icons/IconSettings'
import { vibrate } from '@/src/lib/vibration'
import { IconArrow_2 } from '../ui/icons/IconArrow'
import { playSound } from '@/src/utils/soundPlayer'

type StartScreenProps = {
    onNext: () => void
}

type HeaderActionConfig = {
    key: string
    icon: ReactNode
    color: 'blue' | 'orange'
    onClick?: () => void
}

type ArrowConfig = {
    key: string
    className: string
    icon: ReactNode
    animate: {
        x: number[]
        y: number[]
        scale: number[]
    }
    delay: number
}

const logoVariants: Variants = {
    hidden: {
        opacity: 0,
        y: -150,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

const arrowVariants: Variants = {
    hidden: {
        opacity: 0,
        scale: 0,
    },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
            delay: 1,
        },
    },
}

const ctaVariants: Variants = {
    hidden: {
        opacity: 0,
        y: 150,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

const arrowBounceTransition = {
    duration: 3.6,
    repeat: Infinity,
    repeatType: 'loop' as const,
    ease: 'easeInOut' as const,
}


const StartScreen = observer(function StartScreen({ onNext }: StartScreenProps) {
    const gameData = store.getCachedQuery(GetGameDocument)
    const isStele = useSteleBreakpoint();

    useSlideInWooshSound()

    // const hasStartedRef = useRef(false)
    // const handleStart = useCallback(() => {
    //     if (hasStartedRef.current) return
    //     hasStartedRef.current = true
    //     vibrate(40)
    //     onNext()
    // }, [onNext])
    useEffect(() => {

    })


    const arrowConfigs: ArrowConfig[] = [
        {
            key: 'arrow-2',
            className: 'top-0 left-8 absolute',
            icon: <IconArrow_2 width={isStele ? '200' : '50'} height={isStele ? '200' : '50'} />,
            animate: {
                x: isStele ? [-0, 200, -0] : [-40, 35, -40],
                y: isStele ? [-90, 0, -90] : [-24, 18, -24],
                scale: [1, 1.4, 1],
            },
            delay: 1.2,
        },
    ]

    useEffect(() => {

    })

    return (
        <motion.div
            className='absolute inset-0 flex flex-col justify-between items-center p-8 pointer-events-none'
            variants={containerVariants}
            initial='hidden'
            animate='visible'
        >
            {!isStele ? (
                // iOS Safari only unlocks Web Audio on `click`, not `touchstart` / `mousedown`
                <button
                    type='button'
                    className='z-0 absolute inset-0 cursor-pointer pointer-events-auto'
                    onClick={() => { vibrate(40); onNext(); playSound('characterHighFive') }}
                    aria-label='Weiter zum nächsten Screen'
                />
            ) : (
                <button
                    type='button'
                    className='z-0 absolute inset-0 cursor-pointer pointer-events-auto'
                    onMouseDown={() => { vibrate(40); onNext(); playSound('characterHighFive') }}
                    onTouchStart={() => { vibrate(40); onNext(); playSound('characterHighFive') }}
                    aria-label='Weiter zum nächsten Screen'
                />
            )}

            {!isStele && (
                <motion.div variants={logoVariants} className='z-10 relative w-full max-h-min pointer-events-none'>
                    <Logo variant={'default'} />
                </motion.div>
            )}

            {!isStele &&
                <TabControls variants={tabContainerVariants} position={'right'}>
                    <motion.div variants={tabButtonVariants}>
                        <ButtonIcon color='blue' icon={<IconSettings width='100%' height='100%' />} onClick={() => store.openSettings()} />
                    </motion.div>
                </TabControls>
            }

            <motion.div
                variants={arrowVariants}
                className={clsx(
                    '-top-1/7 left-2/11 z-50 absolute flex justify-start items-center w-62 h-full',
                    'stele:w-300 stele:-top-1/7 stele:left-1/5'
                )}
            >
                <div className={clsx(
                    'relative',
                    'w-34 h-34 mb-12',
                    'stele:w-170 stele:h-179 stele:mt-100 stele:left-36'
                )}>
                    {arrowConfigs.map((arrow) => (
                        <motion.div
                            key={arrow.key}
                            className={arrow.className}
                            animate={arrow.animate}
                            transition={{
                                ...arrowBounceTransition,
                                delay: arrow.delay,
                            }}
                        >
                            {arrow.icon}
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className={clsx(
                    'z-20 absolute flex flex-col justify-center items-center w-full pointer-events-none',
                    'bottom-8',
                    'stele:bottom-32'
                )}
                variants={ctaVariants}
                aria-hidden
            >
                <motion.div
                    initial={{ scale: 1 }}
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{
                        duration: 0.5,
                        delay: 2.5,
                        repeatDelay: 2.0,
                        repeat: Infinity,
                        repeatType: 'loop',
                        ease: 'easeInOut',
                    }}
                >
                    <TextHeadline
                        className='shrink-0'
                        fontSize={isStele ? 178 : 32}
                        leading={0}
                        colorPreset='red'
                    >
                        {gameData?.game?.StartScreenHeadline ?? ''}
                    </TextHeadline>
                </motion.div>
                <TextHeadline className='shrink-0' fontSize={isStele ? 178 : 32} leading={1} colorPreset='red'>
                    Jetzt bist du dran!
                </TextHeadline>
                {isStele ? <PreloaderTippCarousel /> : null}
            </motion.div>
        </motion.div>
    )
})

export default StartScreen


